import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Globe,
  MagnifyingGlass,
  QrCode,
} from "@phosphor-icons/react";
import PublicHeader from "@/components/PublicHeader";

export default function PublicLanding() {
  const navigate = useNavigate();
  const [certId, setCertId] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const v = certId.trim().toUpperCase();
    if (!v) {
      setError("Enter a certificate number.");
      return;
    }
    setError("");
    navigate(`/verify/${encodeURIComponent(v)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <PublicHeader />

      <section className="relative">
        <div className="absolute inset-0 brand-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-slate-400" />
              <span className="label-eyebrow">Independent aviation certificate verification</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-slate-900 leading-[0.95]">
              Verify any<br />
              <span className="text-[#0B2046]">aviation certificate.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              IAVA is an independent <b>certificate verification platform</b>. We do not issue,
              grant or train — we only confirm whether a certificate registered against
              its unique number and QR code is authentic, active and currently valid.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border border-slate-200 shadow-[0_8px_32px_rgba(11,32,70,0.06)] p-8 lg:p-10">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow">Public verification</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-mono-plex text-emerald-700 bg-emerald-50 px-2 py-1">
                  <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full" /> ACTIVE
                </span>
              </div>

              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-4">
                Verify a certificate
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                Enter the certificate number printed on the document or scan its QR code.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-4">
                <label className="label-eyebrow block">Certificate Number</label>
                <div className="flex">
                  <input
                    data-testid="verify-input-id"
                    value={certId}
                    onChange={(e) => setCertId(e.target.value)}
                    placeholder="IAVA-2026-XXXXXX"
                    className="flex-1 px-4 py-3.5 border border-slate-300 font-mono-plex tracking-wider text-[15px] focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] bg-white"
                  />
                  <button
                    data-testid="verify-submit-button"
                    type="submit"
                    className="px-5 bg-[#0B2046] text-white hover:bg-[#113066] transition-colors inline-flex items-center gap-2 font-medium"
                  >
                    Verify <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
                {error && (
                  <div data-testid="verify-input-error" className="text-sm text-red-600">{error}</div>
                )}
              </form>

              <div className="mt-7 pt-7 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <QrCode size={14} /> QR codes auto-detected
                </span>
                <span className="font-mono-plex">Independent registry</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="label-eyebrow">/ 01 — Method</span>
              <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-4">
                Verification in<br />three steps.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                IAVA is an independent verification platform. We do not produce, grant
                or train credentials — we only confirm whether the certificate
                presented to you is authentic, active and currently valid against the
                registry.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
              <Step n="01" title="Lookup" icon={<MagnifyingGlass size={20} />}
                body="Certificate number or QR scan resolves directly against the IAVA registry." />
              <Step n="02" title="Validate" icon={<ShieldCheck size={20} />}
                body="Issuance date, expiry and revocation status are cross-checked in real time." />
              <Step n="03" title="Verdict" icon={<Globe size={20} />}
                body="A clear verification result — authentic, expired, revoked, or not found." />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Step({ n, title, body, icon }) {
  return (
    <div className="border border-slate-200 p-6 bg-white hover:border-[#0B2046] transition-colors group">
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-xs text-slate-500">{n}</span>
        <span className="text-slate-400 group-hover:text-[#0B2046] transition-colors">{icon}</span>
      </div>
      <div className="font-display text-xl font-bold tracking-tight text-slate-900 mt-6">{title}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
